import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import Select, { TOption } from "@components/Select";
import { useLang } from "@src/hooks/useLang";
import { WeightUnit, AreaUnit } from "@services/demosService";

/**
 * Account-scoped settings for pushing yield maps to an integration (John Deere
 * or Cropwise — both answer the same shape on GET/PUT `/settings`).
 */
export interface SyncSettings {
  autoSyncYields: boolean;
  /** WeightUnit value, e.g. "t", "bu". */
  weight: string;
  /** AreaUnit value, e.g. "ha", "ac". */
  area: string;
  /** Pounds per bag when weight is "bag" (null → server default 100). */
  poundsPerBag?: number | null;
  /** Pounds per bushel when weight is "bu" (null → server default 60). */
  poundsPerBushel?: number | null;
}

const DEFAULT_SETTINGS: SyncSettings = {
  autoSyncYields: false,
  weight: WeightUnit.Ton,
  area: AreaUnit.Hectare,
  poundsPerBag: null,
  poundsPerBushel: null,
};

/**
 * Load and save one integration's sync settings.
 *
 * The backend PUT replaces the whole row, so every save sends the WHOLE object
 * with the one change applied. The cards used to send only
 * {autoSyncYields, weight, area}, which nulled the stored pounds on every
 * toggle — every bushel sync ran on 60 lb (corn is 56).
 *
 * Nothing saves until the settings have loaded: saving the defaults after a
 * failed load would reset the account's units to t/ha.
 */
export function useIntegrationSyncSettings(
  connected: boolean,
  get: () => Promise<SyncSettings>,
  put: (settings: SyncSettings) => Promise<SyncSettings>
) {
  const [settings, setSettings] = useState<SyncSettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    get()
      .then((s) => {
        if (cancelled) return;
        setSettings({ ...DEFAULT_SETTINGS, ...s });
        setLoaded(true);
      })
      .catch(() => {
        /* stay unloaded: the controls are inert rather than saving defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [connected, get]);

  const save = async (patch: Partial<SyncSettings>) => {
    if (!loaded || saving) return;
    const prev = settings;
    const next = { ...settings, ...patch };
    setSettings(next); // optimistic
    setSaving(true);
    try {
      setSettings({ ...DEFAULT_SETTINGS, ...(await put(next)) });
    } catch {
      setSettings(prev); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  return { settings, loaded, saving, save };
}

const UnitsRow = styled.div`
  display: flex;
  gap: 12px;
  max-width: 360px;
`;

const UnitField = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

/**
 * A pounds-per-unit box that saves on blur, not per keystroke. Empty means
 * "use the server default"; anything that isn't a positive number is
 * discarded and the stored value comes back.
 */
const PoundsField: React.FC<{
  label: string;
  hint: string;
  value: number | null | undefined;
  placeholder: number;
  disabled: boolean;
  onCommit: (value: number | null) => void;
}> = ({ label, hint, value, placeholder, disabled, onCommit }) => {
  const [text, setText] = useState(value != null ? String(value) : "");
  useEffect(() => setText(value != null ? String(value) : ""), [value]);

  const commit = () => {
    const trimmed = text.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && !(Number.isFinite(parsed) && parsed > 0)) {
      setText(value != null ? String(value) : "");
      return;
    }
    if (parsed !== (value ?? null)) onCommit(parsed);
  };

  return (
    <UnitField style={{ maxWidth: 360 }}>
      <Text size={12} color="#899093" style={{ marginBottom: 4 }}>
        {label}
      </Text>
      <Input
        type="number"
        min="1"
        step="1"
        value={text}
        placeholder={String(placeholder)}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
      />
      <Text size={12} color="#899093" style={{ marginTop: 4 }}>
        {hint}
      </Text>
    </UnitField>
  );
};

/**
 * Weight and area pickers for an integration's sync, plus the pounds per
 * bushel / bag when those units are picked. One account-wide value — the
 * sync is not per crop.
 */
export const SyncUnitsFields: React.FC<{
  settings: SyncSettings;
  disabled: boolean;
  onChange: (patch: Partial<SyncSettings>) => void;
}> = ({ settings, disabled, onChange }) => {
  const lang = useLang();
  const weightOptions: TOption[] = [
    { value: WeightUnit.Ton, title: lang.settings.ton },
    { value: WeightUnit.Centner, title: lang.settings.centner },
    { value: WeightUnit.Kilogram, title: lang.settings.kilograms },
    { value: WeightUnit.Pound, title: lang.settings.pounds },
    { value: WeightUnit.ShortTon, title: lang.settings.shortTon },
    { value: WeightUnit.Bushel, title: lang.settings.bushels },
    { value: WeightUnit.Bag, title: lang.settings.bag },
  ];
  const areaOptions: TOption[] = [
    { value: AreaUnit.Hectare, title: lang.settings.hectares },
    { value: AreaUnit.Acre, title: lang.settings.acres },
  ];

  return (
    <>
      <UnitsRow>
        <UnitField>
          <Text size={12} color="#899093" style={{ marginBottom: 4 }}>
            {lang.settings.weightUnit}
          </Text>
          <Select
            value={settings.weight}
            options={weightOptions}
            onChange={(value) => onChange({ weight: value })}
          />
        </UnitField>
        <UnitField>
          <Text size={12} color="#899093" style={{ marginBottom: 4 }}>
            {lang.settings.areaUnit}
          </Text>
          <Select
            value={settings.area}
            options={areaOptions}
            onChange={(value) => onChange({ area: value })}
          />
        </UnitField>
      </UnitsRow>
      {settings.weight === WeightUnit.Bushel && (
        <>
          <SizedBox height={12} />
          <PoundsField
            label={lang.settings.poundsPerBushel}
            hint={lang.settings.poundsPerBushelDescription}
            value={settings.poundsPerBushel}
            placeholder={60}
            disabled={disabled}
            onCommit={(poundsPerBushel) => onChange({ poundsPerBushel })}
          />
        </>
      )}
      {settings.weight === WeightUnit.Bag && (
        <>
          <SizedBox height={12} />
          <PoundsField
            label={lang.settings.poundsPerBag}
            hint={lang.settings.poundsPerBagDescription}
            value={settings.poundsPerBag}
            placeholder={100}
            disabled={disabled}
            onCommit={(poundsPerBag) => onChange({ poundsPerBag })}
          />
        </>
      )}
    </>
  );
};
